import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { NodeItem, IdType, TreeOptionsType } from '../ReactreeUtils.js';
import ReactreeNode from '../ReactreeNode';
import Translate from '../../../core/Translate';

const DEFAULT_NEW_ID_PREFIX = 'newnode';

class ReactreeRootNode extends Component {
	constructor(props) {
		super(props);

		this.listeners = {};
		this.createTreeStructure = this.createTreeStructure.bind(this);
		this.getNode = this.getNode.bind(this);
		this.getActiveNodeId = this.getActiveNodeId.bind(this);
		this.isActiveNodeOnClick = this.isActiveNodeOnClick.bind(this);
		this.isExpandRootOnInit = this.isExpandRootOnInit.bind(this);
		this.isDisableDropdown = this.isDisableDropdown.bind(this);
		this.isDisableDrag = this.isDisableDrag.bind(this);
		this.isDisableDrop = this.isDisableDrop.bind(this);
		this.subscribe = this.subscribe.bind(this);
		this.unsubscribe = this.unsubscribe.bind(this);
		this.notifyChange = this.notifyChange.bind(this);

		this.addNode = this.addNode.bind(this);
		this.renameNode = this.renameNode.bind(this);
		this.setEditMode = this.setEditMode.bind(this);
		this.removeNode = this.removeNode.bind(this);
		this.getAllDescendantIds = this.getAllDescendantIds.bind(this);
		this.moveNode = this.moveNode.bind(this);

		this.nodes = this.createTreeStructure(JSON.parse(this.props.nodeJson));
		this.activeNodeId = this.props.activeNodeId;
	}

	getChildContext() {
		return {
			getNode: this.getNode,
			getActiveNodeId: this.getActiveNodeId,
			isActiveNodeOnClick: this.isActiveNodeOnClick,
			isExpandRootOnInit: this.isExpandRootOnInit,
			isDisableDropdown: this.isDisableDropdown,
			isDisableDrag: this.isDisableDrag,
			isDisableDrop: this.isDisableDrop,
			subscribe: this.subscribe,
			unsubscribe: this.unsubscribe,
			notifyChange: this.notifyChange,
			addNode: this.addNode,
			renameNode: this.renameNode,
			setEditMode: this.setEditMode,
			removeNode: this.removeNode,
			moveNode: this.moveNode
		};
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.nodeJson !== this.props.nodeJson) {
			this.nodes = this.createTreeStructure(JSON.parse(nextProps.nodeJson));
			this.notifyChange(undefined);
		}

		if (nextProps.activeNodeId !== this.props.activeNodeId) {
			this.activeNodeId = nextProps.activeNodeId;
			this.notifyChange(nextProps.activeNodeId);
			this.notifyChange(this.props.activeNodeId);
		}
	}

	shouldComponentUpdate(nextProps, nextState) {
		return (nextProps.nodeJson !== this.props.nodeJson);
	}

	getNode(id) {
		return JSON.parse(JSON.stringify(this.nodes[id]));
	}

	getActiveNodeId() {
		return this.activeNodeId;
	}

	isActiveNodeOnClick() {
		return this.props.options.activeNodeOnClick;
	}

	isExpandRootOnInit() {
		return this.props.options.expandRootOnInit;
	}

	isDisableDropdown(id) {
		if (typeof this.props.options.disableDropdown === 'function') {
			return this.props.options.disableDropdown.call(this, this.nodes[id]);
		}
		return this.props.options.disableDropdown;
	}

	isDisableDrag(id) {
		if (typeof this.props.options.disableDrag === 'function') {
			return this.props.options.disableDrag.call(this, this.nodes[id]);
		}
		return this.props.options.disableDrag;
	}

	isDisableDrop(id) {
		if (typeof this.props.options.disableDrop === 'function') {
			return this.props.options.disableDrop.call(this, this.nodes[id]);
		}
		return this.props.options.disableDrop;
	}

	subscribe(id, listener) {
		this.listeners[id] = listener;
	}

	unsubscribe(id) {
		delete this.listeners[id];
	}

	notifyChange(id) {
		if (typeof this.listeners[id] === 'function') {
			this.listeners[id]();
		}
	}

	addNode(parent) {
		const node = NodeItem.parse({
			id: `${DEFAULT_NEW_ID_PREFIX}-${(new Date()).getTime()}`,
			name: '',
			parent: parent,
			options: {
				isEditingMode: true,
				isNew: true
			}
		});

		if (this.props.onBeforeAdd(node)) {
			this.nodes[parent].children = [node.id, ...this.nodes[parent].children];
			this.nodes[node.id] = node;
			this.notifyChange(parent);
			return true;
		}
		return false;
	}

	onAddSuccess(prevId, { id, type, options = {} }) {
		const node = this.nodes[prevId];
		if (!!node) {
			if (id) {
				if (typeof id === 'string' || typeof id === 'number') {
					setTimeout(function () {
						const prevParentNode = this.nodes[node.parent];
						const children = prevParentNode.children;
						children[children.indexOf(prevId)] = id;
						node.id = id;
						node.type = type;
						node.options = Object.assign(node.options, options);
						delete node.options.isNew;
						this.nodes[id] = node;
						delete this.nodes[prevId];

						console.log(node);

						const descendantIds = this.getAllDescendantIds(id);
						for (const descendantId of descendantIds) {
							this.nodes[descendantId].parent = id;
						}
						this.notifyChange(node.parent);
					}.bind(this));
				} else {
					console.log('Type of id should be (\'string\' or \'number\').', `Current type of id : ${typeof id}`);
				}
			}
		}
	}

	renameNode(id, name, force) {
		const node = this.nodes[id];
		if (!!node && (force || this.props.onBeforeRename(node, name))) {
			node.name = name;
			node.options.isEditingMode = false;
			if (force) {
				this.props.onAfterAdd(node, this.onAddSuccess.bind(this, id));
			} else {
				this.props.onAfterRename(node);
			}
			this.notifyChange(id);
			return true;
		}
		return false;
	}

	setEditMode(id, isEditingMode) {
		const node = this.nodes[id];
		if (!!node) {
			node.options.isEditingMode = isEditingMode;
			this.notifyChange(id);
			return true;
		}
		return false;
	}

	removeNode(id, force) {
		const target = this.nodes[id];
		const parent = this.nodes[id].parent;
		const parentNode = this.nodes[parent];

		if (!!target && (force || this.props.onBeforeRemove(target))) {
			const descendantIds = [id, ...this.getAllDescendantIds(id)];
			for (const descendantId of descendantIds) {
				delete this.nodes[descendantId];
			}
			parentNode.children = parentNode.children.filter((child) => child !== id);
			if (!force) {
				this.props.onAfterRemove(target);
			}
			this.notifyChange(parent);
			return true;
		}
		return false;
	}

	getAllDescendantIds(id) {
		return this.nodes[id].children.reduce((prev, child) => [...prev, child, ...this.getAllDescendantIds(child)], []);
	}

	moveNode(id, srcParent, destParent) {
		const node = this.nodes[id];
		const srcNode = this.nodes[srcParent];
		const destNode = this.nodes[destParent];

		if (!!node && this.props.onBeforeMove(node)) {
			node.parent = destParent;
			srcNode.children = srcNode.children.filter((child) => child !== id);
			destNode.children = [id, ...destNode.children].sort(function(a, b) {
				const node1 = this.nodes[a];
				const node2 = this.nodes[b];

				if (typeof this.props.options.orderPredicate === 'function') {
					const predicate = this.props.options.orderPredicate;
					const val1 = predicate(node1);
					const val2 = predicate(node2);

					if (val1 < val2) {
						return 1;
					} else if (val1 > val2) {
						return -1;
					}
				}
				const name1 = node1.name.toUpperCase();
				const name2 = node2.name.toUpperCase();

				return name1 < name2 ? -1 : (name1 > name2 ? 1 : 0);
			}.bind(this));
			this.notifyChange(srcParent);
			this.notifyChange(destParent);
			this.props.onAfterMove(node);
			return true;
		}
		return false;
	}

	createTreeStructure(nodes) {
		const map = {
			undefined: NodeItem.parse({
				name: this.props.options.rootName,
				children: []
			})
		};

		for (const node of nodes) {
			const id = node['id'];
			node.children = node.children || [];

			if (!map[id]) {
				map[id] = node;
			} else {
				node.children = map[id].children;
				map[id] = node;
			}

			if (map[node['parent']]) {
				map[node['parent']].children.push(node.id);
			} else {
				map[node['parent']] = {
					children: [node.id]
				}
			}
		}

		return map;
	}

	render() {
		return (
			<ul>
				{<ReactreeNode isRoot />}
			</ul>
		);
	}
}

ReactreeRootNode.childContextTypes = {
	getNode: PropTypes.func,
	getActiveNodeId: PropTypes.func,
	isActiveNodeOnClick: PropTypes.func,
	isExpandRootOnInit: PropTypes.func,
	isDisableDropdown: PropTypes.func,
	isDisableDrag: PropTypes.func,
	isDisableDrop: PropTypes.func,
	subscribe: PropTypes.func,
	unsubscribe: PropTypes.func,
	notifyChange: PropTypes.func,
	addNode: PropTypes.func,
	renameNode: PropTypes.func,
	setEditMode: PropTypes.func,
	removeNode: PropTypes.func,
	moveNode: PropTypes.func
};

ReactreeRootNode.propTypes = {
	nodes: PropTypes.array,
	activeNodeId: IdType,
	onAfterAdd: PropTypes.func,
	onBeforeAdd: PropTypes.func,
	onAfterRename: PropTypes.func,
	onBeforeRename: PropTypes.func,
	onAfterRemove: PropTypes.func,
	onBeforeRemove: PropTypes.func,
	onAfterMove: PropTypes.func,
	onBeforeMove: PropTypes.func,
	options: TreeOptionsType
};

export default ReactreeRootNode;


// WEBPACK FOOTER //
// ./resources/WEB-INF/src/ui/Reactree/ReactreeRootNode/index.jsx